<script lang="ts" module>
  // TODO @ioss: import { isTeoError, TeoError } from '@/teoCore/errors/TeoError';

  import type { GeneratorTemplate } from '@/teoCore/TeoApi';
  import type { DropdownCategoryGrouped } from '@/teoCore/text/GeneratorUiHandler.helpers';
  import { useClickOutside } from '@/ui/base/hooks/useClickOutside';
  import LinkButton from '@/ui/elements/buttons/LinkButton.svelte';
  import DropDown from '@/ui/elements/dropdown/DropDown.svelte';
  import type { DropDownItem } from '@/ui/elements/dropdown/DropDown/DropDownSelect.svelte';
  import { useHasViewType } from '@/ui/state/use-text-state.svelte';
  import { useReactiveState } from '@/utils/reactive-store/svelte';
  import { useActiveText } from '@/utils/use-teo-event-observer.svelte';

  import ConfigCard from './cmps/ConfigCard.svelte';
  import ConfigMenuBtn from './cmps/ConfigMenuBtn.svelte';

  function templateToDropDownItem(template: GeneratorTemplate): DropDownItem {
    return {
      label: template.title,
      value: template.id,
    };
  }

  function toCategory(
    dropdown: DropdownCategoryGrouped,
    template: GeneratorTemplate | null,
    selectedCategory: string | null,
    selectedOptions: Record<string, string | undefined>,
  ) {
    const id = dropdown.id;
    const selectedOption = template?.options[id]?.find(
      (option) => String(option.id) === selectedOptions[id],
    );
    return {
      label: selectedOption?.title ?? dropdown.label,
      category: id,
      highlight: !!selectedOption,
      active: selectedCategory === id || selectedCategory === 'ALL',
    };
  }
</script>

<script lang="ts">
  let activeText = useActiveText();
  let hidden$ = useHasViewType('new');
  let hidden = $derived($hidden$);

  let generatorUI = activeText.current.generatorUi.withInit();
  let S = useReactiveState(generatorUI.state);

  let selectedCategory = $state(null as string | null);

  let templates = $derived.by(() => {
    return {
      items: $S.templates.available.map(templateToDropDownItem),
      loading: $S.templates.loading,
    };
  });

  let categories = $derived.by(() => {
    const template = $S.currentTemplate;
    const selectedOptions = $S.selectedOptions;

    return $S.dropdownCategories.map((dropdown) =>
      toCategory(dropdown, template, selectedCategory, selectedOptions),
    );
  });

  let lastSelectedTemplateId = $state($S.currentTemplate?.id);
  $effect(() => {
    if ($S.currentTemplate === null) {
      selectedCategory = null;
    } else if ($S.currentTemplate?.id !== lastSelectedTemplateId) {
      selectedCategory = 'ALL';
    }
    lastSelectedTemplateId = $S.currentTemplate?.id;
  });

  $effect(() => {
    if ($S.loading) {
      selectedCategory = null;
    }
  });

  function toggleOpenAll() {
    selectedCategory = selectedCategory ? null : 'ALL';
  }

  const closeOnClickOutside = useClickOutside(() => {
    selectedCategory = null;
  });
</script>

{#if !hidden}
  <div
    class="fc-xc gap-4 rounded-md bg-plain px-4 pb-3 pt-4 shadow-card transition-all"
    use:closeOnClickOutside
  >
    <div class="fc-ns gap-1">
      <div class="fr-csb gap-2 text-lsm font-normal">
        <DropDown
          class=""
          items={templates.items}
          selected={$S.currentTemplate?.id}
          onItemClick={(val) => generatorUI.selectTemplate(val)}
          ariaLabel="Vorlage auswählen"
          placeholder={templates.loading ? 'Vorlagen werden geladen...' : 'Vorlage auswählen'}
          disabled={templates.loading}
        />
        {#if $S.dropdownCategories.length}
          <div class="p-1">
            <ConfigMenuBtn onChange={toggleOpenAll} active={!!selectedCategory} />
          </div>
        {/if}
      </div>
      <div class="frw-cs f-1 w-full flex-wrap gap-x-2 text-lsm font-normal">
        {#if $S.dropdownCategories}
          {#each categories as category, index (category.label)}
            <div class="px-1">
              <LinkButton
                highlight={category.highlight}
                onclick={() => {
                  selectedCategory = category.category;
                }}
                active={category.category === selectedCategory}>{category.label}</LinkButton
              >
            </div>

            {#if index !== $S.dropdownCategories.length - 1}
              <span role="separator">•</span>
            {/if}
          {/each}
        {/if}
      </div>
    </div>

    {#if selectedCategory}
      <div class="fc gap-3 border-t border-c-divider pt-3 transition-all">
        {#each $S.dropdownCategories as dropdown (dropdown.id)}
          {#if selectedCategory === 'ALL' || selectedCategory === dropdown.id}
            <ConfigCard
              groupName={dropdown.id}
              title={dropdown.label}
              groups={dropdown.groups}
              value={$S.selectedOptions[dropdown.id]}
              onchange={(value) => $S.selectOption(dropdown.id, value)}
            />
          {/if}
        {/each}
      </div>
    {/if}
  </div>
{/if}
